import { defineStore } from 'pinia';
import { DuoService } from '@/services/DuoService';

interface DuoAuthState {
    isVisible: boolean;
    isLoading: boolean;
    error: string | null;
    currentAction: string | null;
    previousAction: string | null;
    isNavigatingToDuo: boolean;
    duoCode: string | null;
    state: string | null;
}

export const useDuoAuthStore = defineStore('duoAuth', {
    state: (): DuoAuthState => ({
        isVisible: false,
        isLoading: false,
        error: null,
        currentAction: null,
        previousAction: null,
        isNavigatingToDuo: false,
        duoCode: null,
        state: null
    }),

    getters: {
        getPreviousAction: (state) => {
            return {
                action: state.previousAction,
                isAvailable: !!(state.previousAction && pendingCallbacks.previousActionCallback)
            };
        }
    },

    actions: {
        /**
         * Check for and handle Duo authentication callback on page load
         */
        async checkDuoCallback(): Promise<boolean> {
            const duoService = DuoService.getInstance();

            if (duoService.isAuthCallback()) {
                this.isLoading = true;
                this.error = null;

                try {
                    const callbackParams = duoService.getCallbackParams();

                    const result = await duoService.handleAuthCallback(callbackParams);

                    if (result.success) {
                        this.handleDuoSuccess(result.username || '', result.decodedToken, result.duoCode, result.state);
                        // Clean up the URL
                        window.history.replaceState({}, document.title, window.location.pathname);
                        return true;
                    } else {
                        console.error('Duo callback failed:', result.error);
                        this.handleDuoError(result.error || 'Authentication callback failed');
                        return false;
                    }
                } catch (error) {
                    console.error('Error handling Duo callback:', error);
                    const errorMessage = error instanceof Error ? error.message : 'Callback processing failed';
                    this.handleDuoError(errorMessage);
                    return false;
                } finally {
                    this.isLoading = false;
                }
            } else {
            }

            return false;
        },

        /**
         * Set a callback to execute after successful Duo authentication
         */
        setPendingCallback(callback: () => void) {
            pendingCallbacks.pendingActionCallback = callback;
        },

        /**
         * Require Duo 2FA before executing an action
         */
        async requireDuoAuth(action: string, redirectUrl?: string): Promise<void> {
            if (!action) {
                this.error = 'Action is required';
                return;
            }

            try {
                // Store previous action before setting new ones
                this.previousAction = this.currentAction ?? action;

                // Store the callback for after navigation returns
                this.currentAction = action;
                this.error = null;
                this.isLoading = true;
                this.isNavigatingToDuo = true;

                // Get Duo service instance
                const duoService = DuoService.getInstance();

                // Perform Duo authentication - this will navigate away from the page
                const result = await duoService.authenticateUser(action, redirectUrl);

                // Handle immediate results (mock success or immediate failures)
                if (result.success) {
                    // Mock success case - handle immediately
                    this.handleDuoSuccess(result.username || 'unknown', result.decodedToken);
                } else {
                    // This code will only execute if authentication fails immediately
                    // (e.g., network error, configuration issue)
                    // For real Duo auth, this will be 'Authentication in progress - redirecting to Duo'
                    if (result.error !== 'Authentication in progress - redirecting to Duo') {
                        this.handleDuoError(result.error || 'Authentication failed');
                    }
                }
            } catch (error) {
                console.error('Error requiring Duo auth:', error);
                const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
                this.handleDuoError(errorMessage);
            } finally {
                this.isLoading = false;
                this.isNavigatingToDuo = false;
            }
        },

        /**
         * Handle successful Duo authentication
         */
        handleDuoSuccess(_authenticatedUser: string, decodedToken?: any, duoCode?: string, state?: string) {

            if (decodedToken) {
            }

            // Store duo credentials for use in payment-preferences updates
            this.duoCode = duoCode || null;
            this.state = state || null;

            this.error = null;
            this.isLoading = false;

            // Execute the pending action if there is one
            if (pendingCallbacks.pendingActionCallback) {
                try {
                    pendingCallbacks.pendingActionCallback();
                } catch (error) {
                    console.error('Error executing pending action:', error);
                    this.error = 'Failed to execute action after authentication';
                } finally {
                    pendingCallbacks.pendingActionCallback = null;
                }
            } else {
                // If no pending callback, the authentication was successful
                // but we may need to redirect the user to the appropriate page
            }
        },

        /**
         * Handle Duo authentication error
         */
        handleDuoError(error: string) {
            console.error('Duo authentication error:', error);
            this.error = error;
            this.isLoading = false;

            // Don't execute the pending action on error
            pendingCallbacks.pendingActionCallback = null;
        },

        /**
         * Handle Duo authentication cancellation
         */
        handleDuoCancel() {
            this.error = null;
            this.isLoading = false;

            // Don't execute the pending action on cancel
            pendingCallbacks.pendingActionCallback = null;
        },

        /**
         * Close the Duo dialog and reset
         */
        closeDuoDialog() {
            this.isVisible = false;
            this.isLoading = false;
            this.error = null;
            pendingCallbacks.pendingActionCallback = null;
        },

        /**
         * Clear previous action (useful for route guards)
         */
        clearPreviousAction() {
            this.previousAction = null;
            pendingCallbacks.previousActionCallback = null;
        },

        /**
         * Clear duo credentials (if needed for logout or page navigation)
         */
        clearDuoCredentials() {
            this.duoCode = null;
            this.state = null;
        },

        /**
         * Check if user has valid authentication for specific actions
         */
        hasValidAuthForActions(actions: string[]): boolean {
            return !!(this.previousAction && actions.includes(this.previousAction));
        }
    }
});

// Store callbacks outside the store to maintain them across store resets
const pendingCallbacks = {
    pendingActionCallback: null as (() => void) | null,
    previousActionCallback: null as (() => void) | null
};
